import { useCallback, useEffect, useRef, useState } from "react";

import type { KoryHaloState } from "@/components/kory/kory-halo";

/** How long the done flash + check holds before the halo settles to idle. */
export const KORY_DONE_MS = 2000;

/**
 * Seam for to-do agent runs: once the to-do action contract exposes a run's
 * `state` and its executor verb on the to-do DTO, the card passes it here and
 * `preparing` / `running` light the working phase with that verb. Until then
 * the card passes `null`.
 */
export type KoryTodoRunSignal = {
  state:
    | "preparing"
    | "awaiting_approval"
    | "running"
    | "waiting_external"
    | "succeeded"
    | "failed"
    | "uncertain"
    | "canceled";
  verb: string | null;
};

export type KoryLiveSignals = {
  /** Voice capture is recording. */
  listening: boolean;
  /** The brief's TTS audio is playing. */
  speaking: boolean;
  /** A chat reply is streaming, or dictation is being transcribed. */
  thinking: boolean;
  /** The morning brief is composing server-side. */
  composing: boolean;
  /** Verb of a CoS action run in flight, if any. */
  actionVerb: string | null;
  todoRun: KoryTodoRunSignal | null;
};

export type KoryLiveState = { state: KoryHaloState; verb: string | null };

const ACTIVE_RUN_STATES = new Set<KoryTodoRunSignal["state"]>(["preparing", "running"]);

/**
 * One phase at a time, in the order the user feels them: their own voice
 * first, then Kory's voice, then real work with a verb, then generation.
 */
export function deriveKoryLiveState(signals: KoryLiveSignals, done: string | null): KoryLiveState {
  if (signals.listening) return { state: "listening", verb: null };
  if (signals.speaking) return { state: "speaking", verb: null };
  if (signals.todoRun && ACTIVE_RUN_STATES.has(signals.todoRun.state)) {
    return { state: "working", verb: signals.todoRun.verb || "Working on your to-do…" };
  }
  if (signals.actionVerb) return { state: "working", verb: signals.actionVerb };
  if (signals.composing) return { state: "working", verb: "Composing your brief…" };
  if (signals.thinking) return { state: "thinking", verb: null };
  if (done !== null) return { state: "done", verb: done };
  return { state: "idle", verb: null };
}

/** Working-line copy for a CoS action run (`verb_object` is the action's own title). */
export function actionRunVerb(verbObject: string | null | undefined, count = 1): string {
  if (count > 1) return `Running ${count} actions…`;
  const verb = verbObject?.trim();
  return verb ? `On it: ${verb}` : "On it…";
}

/**
 * Live halo state for the CoS card. `pulseDone(label)` fires the done phase
 * for a run that actually succeeded; any active phase overrides it.
 */
export function useKoryLiveState(signals: KoryLiveSignals) {
  const [done, setDone] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseDone = useCallback((label: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setDone(label);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setDone(null);
    }, KORY_DONE_MS);
  }, []);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );
  return { ...deriveKoryLiveState(signals, done), pulseDone };
}
